import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardList,
  Flame,
  Handshake,
  Mic,
  Plus,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";
import { useGetIdentity, useGetList } from "ra-core";
import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { ThemeToggle } from "../layout/ThemeToggle";
import { WidgetErrorBoundary } from "../misc/WidgetErrorBoundary";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { findSectorLabel, findStageLabel } from "../meetings/meetingsUtils";
import {
  formatMeetingDate,
  formatMeetingTime,
  formatMoneyMx,
  type Meeting,
} from "../meetings/meetingsUtils";
import { StarRating } from "../meetings/StarRating";

/**
 * Dashboard — rediseñado P3 Bento Spatial.
 *
 * Layout:
 *   ┌─ Header (saludo + toggle tema)
 *   ├─ Grid 2x2 de widgets:
 *   │     [ Empresas ]   [ Contactos ]
 *   │     [ Oportunidades ] [ Juntas ]
 *   └─ Banda inferior con próximas tareas
 *
 * Cada widget muestra:
 *   - Stat principal grande (total)
 *   - Insight útil (top 3 por algún criterio o breakdown)
 *   - CTA a la sección
 *
 * Cuando un recurso está vacío, el widget muestra un empty state suave
 * con CTA "Crear primero".
 */
export const Dashboard = () => {
  const { identity } = useGetIdentity();
  const firstName = (identity?.fullName ?? "").split(" ")[0] || "Hola";
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";

  return (
    <div className="flex flex-col min-h-screen">
      <header className="px-7 pt-6 pb-4 flex items-center gap-3.5 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="m-0 text-[28px] font-bold tracking-[-0.025em]">
            {greeting}, {firstName} 👋
          </h1>
          <div className="text-sm" style={{ color: "var(--musco-muted)" }}>
            Resumen comercial al{" "}
            {new Date().toLocaleDateString("es-MX", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </div>
        </div>
        <ThemeToggle />
      </header>

      <div className="flex-1 overflow-auto px-7 pb-7">
        {/* Grid 2x2 de widgets principales — cada uno envuelto en su
            propio ErrorBoundary para que un fallo no tire todo el dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WidgetErrorBoundary label="Empresas">
            <CompaniesWidget />
          </WidgetErrorBoundary>
          <WidgetErrorBoundary label="Contactos">
            <ContactsWidget />
          </WidgetErrorBoundary>
          <WidgetErrorBoundary label="Oportunidades">
            <DealsWidget />
          </WidgetErrorBoundary>
          <WidgetErrorBoundary label="Juntas">
            <MeetingsWidget />
          </WidgetErrorBoundary>
        </div>

        {/* Sección inferior: próximas tareas */}
        <div className="mt-4">
          <WidgetErrorBoundary label="Próximas tareas">
            <UpcomingTasksWidget />
          </WidgetErrorBoundary>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Widget: Empresas
// ============================================================

function CompaniesWidget() {
  const { companySectors } = useConfigurationContext();
  const { data: companies, total, isPending } = useGetList(
    "companies_summary",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
  );

  const breakdown = useMemo(() => {
    if (!companies) return [];
    const bySector = new Map<string, number>();
    for (const c of companies as any[]) {
      const key = c.sector ?? "sin-sector";
      bySector.set(key, (bySector.get(key) ?? 0) + 1);
    }
    return Array.from(bySector.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([sector, count]) => ({
        sector,
        count,
        label: findSectorLabel(companySectors, sector),
      }));
  }, [companies, companySectors]);

  const recent = (companies ?? []).slice(0, 3);

  return (
    <WidgetCard
      icon={<Building2 size={18} />}
      title="Empresas"
      total={total ?? 0}
      totalLabel={total === 1 ? "empresa registrada" : "empresas registradas"}
      ctaLink="/companies"
      ctaLabel="Ver todas"
      emptyState={
        <EmptyHint
          message="Aún no hay empresas en el CRM."
          ctaLink="/companies/create"
          ctaLabel="Crear primera"
        />
      }
      isEmpty={!isPending && (total ?? 0) === 0}
    >
      <Section label="Por sector">
        {breakdown.length === 0 ? (
          <SoftText>Sin sectores aún.</SoftText>
        ) : (
          <div className="flex flex-col gap-1.5">
            {breakdown.map((b) => (
              <BarRow
                key={b.sector}
                label={b.label}
                value={b.count}
                max={total ?? 0}
              />
            ))}
          </div>
        )}
      </Section>

      <Section label="Agregadas recientemente">
        {recent.length === 0 ? (
          <SoftText>—</SoftText>
        ) : (
          <div className="flex flex-col gap-1">
            {recent.map((c: any) => (
              <Link
                key={c.id}
                to={`/companies/${c.id}/show`}
                className="flex items-center justify-between py-1 px-2 rounded-md hover:bg-[var(--musco-panel2)] transition-colors text-sm no-underline"
                style={{ color: "var(--musco-text)" }}
              >
                <span className="truncate font-medium">{c.name}</span>
                <span
                  className="text-xs tnums shrink-0 ml-2"
                  style={{ color: "var(--musco-muted)" }}
                >
                  {c.nb_contacts ?? 0} cont · {c.nb_deals ?? 0} ops
                </span>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </WidgetCard>
  );
}

// ============================================================
// Widget: Contactos
// ============================================================

function ContactsWidget() {
  const { noteStatuses } = useConfigurationContext();
  const { data: contacts, total, isPending } = useGetList(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_seen", order: "DESC" },
    },
  );

  const statusBreakdown = useMemo(() => {
    if (!contacts) return [];
    const byStatus = new Map<string, number>();
    for (const c of contacts as any[]) {
      const key = c.status ?? "none";
      byStatus.set(key, (byStatus.get(key) ?? 0) + 1);
    }
    return noteStatuses
      .map((s) => ({
        value: s.value,
        label: s.label,
        color: s.color,
        count: byStatus.get(s.value) ?? 0,
      }))
      .filter((s) => s.count > 0)
      .slice(0, 4);
  }, [contacts, noteStatuses]);

  const hotContacts = (contacts ?? [])
    .filter((c: any) => c.status === "hot")
    .slice(0, 3);

  return (
    <WidgetCard
      icon={<Users size={18} />}
      title="Contactos"
      total={total ?? 0}
      totalLabel={total === 1 ? "contacto en el CRM" : "contactos en el CRM"}
      ctaLink="/contacts"
      ctaLabel="Ver todos"
      emptyState={
        <EmptyHint
          message="Aún no hay contactos."
          ctaLink="/contacts/create"
          ctaLabel="Crear primer contacto"
        />
      }
      isEmpty={!isPending && (total ?? 0) === 0}
    >
      <Section label="Por estado">
        {statusBreakdown.length === 0 ? (
          <SoftText>Sin estados asignados.</SoftText>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {statusBreakdown.map((s) => (
              <span
                key={s.value}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{
                  background: s.color
                    ? s.color + "22"
                    : "var(--musco-panel2)",
                  color: "var(--musco-text)",
                }}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: s.color ?? "var(--musco-muted)" }}
                />
                {s.label}
                <span className="tnums opacity-70">· {s.count}</span>
              </span>
            ))}
          </div>
        )}
      </Section>

      <Section
        label={
          <span className="inline-flex items-center gap-1.5">
            <Flame size={12} style={{ color: "var(--musco-hot)" }} /> Contactos
            calientes
          </span>
        }
      >
        {hotContacts.length === 0 ? (
          <SoftText>Sin contactos calientes ahora mismo.</SoftText>
        ) : (
          <div className="flex flex-col gap-1">
            {hotContacts.map((c: any) => (
              <Link
                key={c.id}
                to={`/contacts/${c.id}/show`}
                className="flex items-center justify-between py-1 px-2 rounded-md hover:bg-[var(--musco-panel2)] transition-colors text-sm no-underline"
                style={{ color: "var(--musco-text)" }}
              >
                <span className="truncate font-medium">
                  {c.first_name} {c.last_name}
                </span>
                <span
                  className="text-xs truncate ml-2 shrink-0"
                  style={{ color: "var(--musco-muted)" }}
                >
                  {c.company_name ?? "—"}
                </span>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </WidgetCard>
  );
}

// ============================================================
// Widget: Oportunidades
// ============================================================

function DealsWidget() {
  const { dealStages, dealPipelineStatuses, currency } = useConfigurationContext();
  const { data: deals, total, isPending } = useGetList(
    "deals",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "amount", order: "DESC" },
      filter: { "archived_at@is": null },
    },
  );

  const pipelineDeals = (deals ?? []).filter((d: any) =>
    dealPipelineStatuses.includes(d.stage),
  );

  const pipelineValue = pipelineDeals.reduce(
    (s, d: any) => s + (d.amount ?? 0),
    0,
  );

  const byStage = useMemo(() => {
    const m = new Map<string, { count: number; sum: number }>();
    for (const d of pipelineDeals as any[]) {
      const e = m.get(d.stage) ?? { count: 0, sum: 0 };
      e.count += 1;
      e.sum += d.amount ?? 0;
      m.set(d.stage, e);
    }
    return dealStages
      .filter((s) => dealPipelineStatuses.includes(s.value))
      .map((s) => ({
        stage: s.value,
        label: s.label,
        count: m.get(s.value)?.count ?? 0,
        sum: m.get(s.value)?.sum ?? 0,
      }))
      .filter((s) => s.count > 0);
  }, [pipelineDeals, dealStages, dealPipelineStatuses]);

  const topDeals = pipelineDeals.slice(0, 3);

  return (
    <WidgetCard
      icon={<Handshake size={18} />}
      title="Oportunidades"
      total={pipelineDeals.length}
      totalLabel={
        pipelineDeals.length === 1
          ? "oportunidad activa"
          : "oportunidades activas"
      }
      headlineRight={
        pipelineValue > 0 ? (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-sm tnums"
            style={{
              background: "var(--musco-accent)",
              color: "var(--musco-accent-foreground)",
            }}
          >
            <TrendingUp size={12} /> {formatMoneyMx(pipelineValue, currency)}
          </span>
        ) : null
      }
      ctaLink="/deals"
      ctaLabel="Abrir pipeline"
      emptyState={
        <EmptyHint
          message="Aún no hay oportunidades activas."
          ctaLink="/deals/create"
          ctaLabel="Crear oportunidad"
        />
      }
      isEmpty={!isPending && (total ?? 0) === 0}
    >
      <Section label="Por etapa">
        {byStage.length === 0 ? (
          <SoftText>Pipeline vacío.</SoftText>
        ) : (
          <div className="flex flex-col gap-1.5">
            {byStage.map((s) => (
              <BarRow
                key={s.stage}
                label={s.label}
                value={s.count}
                max={pipelineDeals.length}
                hint={formatMoneyMx(s.sum, currency)}
              />
            ))}
          </div>
        )}
      </Section>

      <Section label="Top oportunidades">
        {topDeals.length === 0 ? (
          <SoftText>—</SoftText>
        ) : (
          <div className="flex flex-col gap-1">
            {topDeals.map((d: any) => (
              <Link
                key={d.id}
                to={`/deals/${d.id}/show`}
                className="flex items-center justify-between py-1 px-2 rounded-md hover:bg-[var(--musco-panel2)] transition-colors text-sm no-underline"
                style={{ color: "var(--musco-text)" }}
              >
                <span className="truncate font-medium">{d.name}</span>
                <span className="ml-2 shrink-0 flex items-center gap-2">
                  <span
                    className="text-xs"
                    style={{ color: "var(--musco-muted)" }}
                  >
                    {findStageLabel(dealStages, d.stage)}
                  </span>
                  <span className="text-xs font-semibold tnums">
                    {formatMoneyMx(d.amount ?? 0, currency)}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </WidgetCard>
  );
}

// ============================================================
// Widget: Juntas
// ============================================================

function MeetingsWidget() {
  const { data: meetings, total, isPending } = useGetList<Meeting>(
    "meetings_summary",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "occurred_at", order: "DESC" },
    },
  );

  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - 7);
  const thisWeek = (meetings ?? []).filter(
    (m) => new Date(m.occurred_at) >= startOfWeek,
  );

  const ratedMeetings = (meetings ?? []).filter(
    (m) => typeof m.rating === "number" && m.rating > 0,
  );
  const avgRating =
    ratedMeetings.length > 0
      ? ratedMeetings.reduce((s, m) => s + (m.rating ?? 0), 0) /
        ratedMeetings.length
      : 0;

  const topRated = [...(meetings ?? [])]
    .filter((m) => typeof m.rating === "number" && m.rating > 0)
    .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
    .slice(0, 3);

  return (
    <WidgetCard
      icon={<Mic size={18} />}
      title="Juntas"
      total={total ?? 0}
      totalLabel={total === 1 ? "junta capturada" : "juntas capturadas"}
      headlineRight={
        thisWeek.length > 0 ? (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-sm tnums"
            style={{
              background: "var(--musco-panel2)",
              color: "var(--musco-text)",
            }}
          >
            {thisWeek.length}{" "}
            {thisWeek.length === 1 ? "esta semana" : "esta semana"}
          </span>
        ) : null
      }
      ctaLink="/meetings"
      ctaLabel="Ver todas"
      emptyState={
        <EmptyHint
          message="Aún no hay juntas capturadas."
          ctaLink="/meetings/create"
          ctaLabel="Capturar primera"
        />
      }
      isEmpty={!isPending && (total ?? 0) === 0}
    >
      {ratedMeetings.length > 0 ? (
        <Section label="Calificación promedio">
          <div className="flex items-center gap-2">
            <StarRating value={Math.round(avgRating)} size={18} readOnly />
            <span className="text-sm font-semibold tnums">
              {avgRating.toFixed(1)}/5
            </span>
            <span className="text-xs" style={{ color: "var(--musco-muted)" }}>
              · sobre {ratedMeetings.length}{" "}
              {ratedMeetings.length === 1 ? "junta" : "juntas"}
            </span>
          </div>
        </Section>
      ) : null}

      <Section label="Mejor calificadas">
        {topRated.length === 0 ? (
          <SoftText>Sin juntas calificadas todavía.</SoftText>
        ) : (
          <div className="flex flex-col gap-1">
            {topRated.map((m) => (
              <Link
                key={m.id}
                to={`/meetings/${m.id}/show`}
                className="flex items-center justify-between py-1 px-2 rounded-md hover:bg-[var(--musco-panel2)] transition-colors text-sm no-underline"
                style={{ color: "var(--musco-text)" }}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Star
                    size={12}
                    fill="var(--musco-accent)"
                    stroke="var(--musco-accent)"
                    className="shrink-0"
                  />
                  <span className="font-semibold tnums">{m.rating}</span>
                  <span
                    className="truncate"
                    style={{ color: "var(--musco-text)" }}
                  >
                    {m.company_name}
                  </span>
                </span>
                <span
                  className="text-xs ml-2 shrink-0"
                  style={{ color: "var(--musco-muted)" }}
                >
                  {formatMeetingDate(m.occurred_at)}
                </span>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {meetings && meetings.length > 0 && topRated.length === 0 ? (
        <Section label="Última junta">
          <Link
            to={`/meetings/${meetings[0].id}/show`}
            className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-[var(--musco-panel2)] transition-colors text-sm no-underline"
            style={{ color: "var(--musco-text)" }}
          >
            <span className="truncate font-medium">
              {meetings[0].company_name}
            </span>
            <span
              className="text-xs ml-2 shrink-0"
              style={{ color: "var(--musco-muted)" }}
            >
              {formatMeetingDate(meetings[0].occurred_at)} ·{" "}
              {formatMeetingTime(meetings[0].occurred_at)}
            </span>
          </Link>
        </Section>
      ) : null}
    </WidgetCard>
  );
}

// ============================================================
// Widget: Próximas tareas (banda inferior)
// ============================================================

function UpcomingTasksWidget() {
  const { data: tasks, isPending } = useGetList(
    "tasks",
    {
      pagination: { page: 1, perPage: 6 },
      sort: { field: "due_date", order: "ASC" },
      filter: { "done_date@is": null },
    },
  );

  const upcoming = (tasks ?? []).slice(0, 6);

  return (
    <div className="musco-panel p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="m-0 text-base font-semibold flex items-center gap-2">
          <ClipboardList
            size={16}
            style={{ color: "var(--musco-muted)" }}
          />
          Próximas tareas
        </h3>
        <Link
          to="/tasks"
          className="text-xs font-semibold inline-flex items-center gap-1"
          style={{ color: "var(--musco-accent2)" }}
        >
          Ver todas <ArrowRight size={12} />
        </Link>
      </div>
      {isPending ? (
        <SoftText>Cargando…</SoftText>
      ) : upcoming.length === 0 ? (
        <div className="text-center py-6">
          <CheckCircle2
            size={26}
            style={{
              color: "var(--musco-success, #18a05a)",
              margin: "0 auto 4px",
            }}
          />
          <p
            className="text-sm m-0"
            style={{ color: "var(--musco-muted)" }}
          >
            Sin tareas pendientes. ¡Bien hecho!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {upcoming.map((t: any) => {
            const due = t.due_date ? new Date(t.due_date) : null;
            const overdue = due && due < new Date();
            return (
              <Link
                key={t.id}
                to={`/contacts/${t.contact_id}/show`}
                className="flex items-start gap-2.5 p-2.5 rounded-lg no-underline hover:bg-[var(--musco-panel2)] transition-colors"
                style={{
                  background: "var(--musco-panel2)",
                  color: "var(--musco-text)",
                }}
              >
                <div
                  className="w-1 self-stretch rounded-full shrink-0"
                  style={{
                    background: overdue
                      ? "var(--musco-hot)"
                      : "var(--musco-accent)",
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.text}</div>
                  <div
                    className="text-xs flex items-center gap-1.5"
                    style={{
                      color: overdue
                        ? "var(--musco-hot)"
                        : "var(--musco-muted)",
                    }}
                  >
                    {due ? (
                      <>
                        {overdue ? "Vencida " : "Vence "}
                        {formatDistance(due, new Date(), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </>
                    ) : (
                      "Sin fecha"
                    )}
                    {t.type ? <span>· {t.type}</span> : null}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Primitivos del Dashboard
// ============================================================

function WidgetCard({
  icon,
  title,
  total,
  totalLabel,
  ctaLink,
  ctaLabel,
  headlineRight,
  emptyState,
  isEmpty,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  total: number;
  totalLabel: string;
  ctaLink: string;
  ctaLabel: string;
  headlineRight?: React.ReactNode;
  emptyState: React.ReactNode;
  isEmpty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="musco-panel p-5 flex flex-col gap-4 min-h-[280px]">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <div
          className="grid place-items-center"
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: "var(--musco-panel2)",
            color: "var(--musco-text)",
          }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="m-0 text-[15px] font-semibold"
            style={{ letterSpacing: "-0.01em" }}
          >
            {title}
          </h3>
        </div>
        <Link
          to={ctaLink}
          className="text-xs font-semibold inline-flex items-center gap-1"
          style={{ color: "var(--musco-accent2)" }}
        >
          {ctaLabel} <ArrowRight size={12} />
        </Link>
      </div>

      {/* Big number row */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div
            className="text-[34px] font-bold tracking-[-0.03em] leading-none tnums"
            style={{ color: "var(--musco-text)" }}
          >
            {total}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--musco-muted)" }}>
            {totalLabel}
          </div>
        </div>
        {headlineRight}
      </div>

      {/* Content */}
      {isEmpty ? (
        emptyState
      ) : (
        <div className="flex flex-col gap-3 mt-1">{children}</div>
      )}
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        className="text-[11px] uppercase tracking-wider font-semibold mb-1.5"
        style={{ color: "var(--musco-muted)" }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function SoftText({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-sm m-0 italic"
      style={{ color: "var(--musco-muted)" }}
    >
      {children}
    </p>
  );
}

function BarRow({
  label,
  value,
  max,
  hint,
}: {
  label: string;
  value: number;
  max: number;
  hint?: string;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="flex-1 min-w-0 truncate">{label}</span>
      <div
        className="relative h-1.5 w-20 rounded-full overflow-hidden shrink-0"
        style={{ background: "var(--musco-panel2)" }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${pct}%`,
            background: "var(--musco-accent)",
          }}
        />
      </div>
      <span
        className="text-xs tnums font-semibold shrink-0 w-6 text-right"
        style={{ color: "var(--musco-muted)" }}
      >
        {value}
      </span>
      {hint ? (
        <span
          className="text-xs tnums shrink-0"
          style={{ color: "var(--musco-muted)" }}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function EmptyHint({
  message,
  ctaLink,
  ctaLabel,
}: {
  message: string;
  ctaLink: string;
  ctaLabel: string;
}) {
  return (
    <div
      className="rounded-xl p-5 text-center mt-1"
      style={{
        background: "var(--musco-panel2)",
        border: "1px dashed var(--musco-line)",
      }}
    >
      <p
        className="text-sm mb-2 m-0"
        style={{ color: "var(--musco-muted)" }}
      >
        {message}
      </p>
      <Link
        to={ctaLink}
        className="musco-btn musco-btn-a inline-flex"
        style={{ fontSize: 12, padding: "6px 10px" }}
      >
        <Plus size={12} /> {ctaLabel}
      </Link>
    </div>
  );
}
